import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Hook to log audit actions. Called internally by other hooks.
 */
export const useAuditLog = () => {
  const mutation = useMutation({
    mutationFn: async ({
      action,
      entityType,
      entityId,
      metadata = {},
    }: {
      action: string;
      entityType: string;
      entityId: string;
      metadata?: Record<string, any>;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("audit_logs").insert({
        user_id: user.id,
        action,
        entity_type: entityType,
        entity_id: entityId,
        metadata,
      });
      if (error) throw error;
    },
  });

  return mutation.mutateAsync;
};

/**
 * Hook to save a content version snapshot before publishing.
 */
export const useContentVersion = () => {
  const mutation = useMutation({
    mutationFn: async ({
      entityType,
      entityId,
      snapshot,
    }: {
      entityType: "page_content" | "content_item";
      entityId: string;
      snapshot: Record<string, any>;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();

      // Keep only last 5 versions per entity
      const { data: existing } = await supabase
        .from("content_versions")
        .select("id")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .order("created_at", { ascending: false });

      if (existing && existing.length >= 5) {
        const idsToDelete = existing.slice(4).map((v) => v.id);
        await supabase
          .from("content_versions")
          .delete()
          .in("id", idsToDelete);
      }

      const { error } = await supabase.from("content_versions").insert({
        entity_type: entityType,
        entity_id: entityId,
        snapshot,
        created_by: user?.id,
      });
      if (error) throw error;
    },
  });

  return mutation.mutateAsync;
};

/**
 * Hook to publish a page_content record.
 * Saves version snapshot, updates status, logs audit.
 */
export const usePublishAction = () => {
  const queryClient = useQueryClient();
  const logAudit = useAuditLog();
  const saveVersion = useContentVersion();

  return useMutation({
    mutationFn: async ({ id, currentContent, currentSeo }: { id: string; currentContent: Record<string, any>; currentSeo: Record<string, any> }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Save version snapshot before publishing
      await saveVersion({
        entityType: "page_content",
        entityId: id,
        snapshot: { content: currentContent, seo: currentSeo },
      });

      // page_content has no updated_by or seo columns
      const { error } = await supabase
        .from("page_content")
        .update({ status: "published" })
        .eq("id", id);
      if (error) throw error;

      await logAudit({
        action: "publish",
        entityType: "page_content",
        entityId: id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-pages"] });
      queryClient.invalidateQueries({ queryKey: ["page-content"] });
      toast.success("Page published");
    },
    onError: (err: any) => toast.error("Publish failed: " + err.message),
  });
};

/**
 * Hook to archive a page_content record (soft delete).
 */
export const useArchiveAction = () => {
  const queryClient = useQueryClient();
  const logAudit = useAuditLog();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("page_content")
        .update({ status: "archived" })
        .eq("id", id);
      if (error) throw error;

      await logAudit({
        action: "archive",
        entityType: "page_content",
        entityId: id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-pages"] });
      queryClient.invalidateQueries({ queryKey: ["page-content"] });
      toast.success("Page archived");
    },
    onError: (err: any) => toast.error("Archive failed: " + err.message),
  });
};

/**
 * Hook to rollback to a previous version.
 */
export const useRollbackVersion = () => {
  const queryClient = useQueryClient();
  const logAudit = useAuditLog();

  return useMutation({
    mutationFn: async ({ versionId, entityType, entityId }: { versionId: string; entityType: string; entityId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Fetch the version snapshot
      const { data: version, error: fetchErr } = await supabase
        .from("content_versions")
        .select("snapshot")
        .eq("id", versionId)
        .single();
      if (fetchErr || !version) throw new Error("Version not found");

      const snapshot = version.snapshot as Record<string, any>;

      if (entityType === "page_content") {
        // page_content has no seo or updated_by columns; store seo inside content
        const restoredContent = { ...snapshot.content, _seo: snapshot.seo || {} };
        const { error } = await supabase
          .from("page_content")
          .update({
            content: restoredContent,
          })
          .eq("id", entityId);
        if (error) throw error;
      }

      await logAudit({
        action: "rollback",
        entityType,
        entityId,
        metadata: { restored_version_id: versionId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-pages"] });
      queryClient.invalidateQueries({ queryKey: ["page-content"] });
      toast.success("Content restored from previous version");
    },
    onError: (err: any) => toast.error("Rollback failed: " + err.message),
  });
};
